import KeyIcon from '@material-design-icons/svg/filled/key.svg?react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { usePublisher } from '../../hooks/usePublisher';
import { getApiKeyManagementPermissionMessage } from '../../utils/apiKeyAccess';
import {
  ApiKeyManagementPanel,
  type PublisherScope,
} from '../PreferencesSettingsPage/ApiKeyManagementPanel';

export const DeveloperApiKeysPage = () => {
  const navigate = useNavigate();
  const { activePublisher, isLoading } = usePublisher();
  const publisherScope: PublisherScope | null = !activePublisher
    ? null
    : {
        publisherId: activePublisher.publisherId,
        publisherType:
          activePublisher.publisherType === 'Organization'
            ? 'organization'
            : 'individual',
      };

  const accessError = publisherScope
    ? getApiKeyManagementPermissionMessage(activePublisher?.canManageTeam)
    : null;

  useEffect(() => {
    if (!accessError) {
      return;
    }

    toast.error('Team management permission required', {
      description: accessError,
      duration: 6000,
    });
    navigate('/dashboard/settings/preferences', {
      replace: true,
    });
  }, [accessError, navigate]);

  const handlePermissionDenied = () => {
    navigate('/dashboard/settings/preferences', {
      replace: true,
    });
  };

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-neutral-900">
        <div className="text-neutral-400">Loading API key access…</div>
      </div>
    );
  }

  if (!publisherScope) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-neutral-900">
        <div className="text-red-500">
          {accessError || 'Failed to load API key access'}
        </div>
      </div>
    );
  }

  if (accessError) {
    return null;
  }

  return (
    <div className="flex h-full w-full flex-col">
      <div className="sticky top-0 z-40 h-[72px] shrink-0 bg-neutral-900">
        <div className="flex h-full items-center px-10">
          <div className="flex items-center gap-2">
            <KeyIcon className="size-5 fill-white" />
            <span className="text-base font-medium text-white">
              Developer API Keys
            </span>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="p-10">
          <div className="mx-auto max-w-2xl">
            <p className="mb-8 text-sm leading-relaxed text-neutral-400">
              Manage API keys for the publishing CLI. New keys are created with
              release-management and store-submission access, and the secret is
              shown only once.
            </p>

            <ApiKeyManagementPanel
              publisherScope={publisherScope}
              onPermissionDenied={handlePermissionDenied}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
